import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productsAPI } from '@/utils/api'; // Ensure this path matches your file structure
import { ProductCard } from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Filter } from 'lucide-react';

export const ProductListingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 10000]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        gender: searchParams.get('gender'),
        category: searchParams.get('category'),
        trending: searchParams.get('trending'),
        is_new: searchParams.get('is_new'),
        tag: searchParams.get('tag'),
        search: searchParams.get('search'),
      };

      // Clean up null values
      Object.keys(params).forEach(key => params[key] === null && delete params[key]);

      const data = await productsAPI.getAll(params);
      setProducts(data);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  }, [searchParams]); // Dependency

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const filteredProducts = products.filter(product => {
    // Ensure we have numbers. Default discount to 0 if missing.
    const rawPrice = parseFloat(product.price) || 0;
    const discount = 0;

    const finalPrice = rawPrice * (1 - discount / 100);

    return finalPrice >= priceRange[0] && finalPrice <= priceRange[1];
  });

  return (
    <div className="min-h-screen py-8 overflow-x-hidden" data-testid="product-listing-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">

          {/* --- FIX 2: Update Title to show Search Query --- */}
          <h1 className="font-outfit font-semibold text-3xl md:text-4xl capitalize">
            {searchParams.get('search')
              ? `Results for "${searchParams.get('search')}"`
              : searchParams.get('gender')
                ? `${searchParams.get('gender')} Collection`
                : searchParams.get('trending')
                  ? 'Trending Now'
                  : searchParams.get('is_new')
                    ? 'New Arrivals'
                    : 'All Products'}
          </h1>

          <Button
            variant="outline"
            className="lg:hidden"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-button"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
        </div>

        <div className="flex gap-8">
          {/* Filters Sidebar */}
          <aside
            className={`
              ${showFilters ? 'fixed inset-0 bg-white z-50 p-4 overflow-y-auto' : 'hidden'} 
              lg:block lg:static lg:w-64 lg:p-0 lg:z-auto lg:bg-transparent lg:overflow-visible flex-shrink-0
            `}
            data-testid="filters-sidebar"
          >
            <div className="lg:hidden flex justify-end mb-4">
                <Button variant="ghost" onClick={() => setShowFilters(false)}>Close X</Button>
             </div>
            <div className="lg:sticky lg:top-24 bg-white rounded-lg border border-border p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Filters</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  data-testid="clear-filters-button"
                >
                  Clear All
                </Button>
              </div>

              {/* Gender Filter */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Gender</Label>
                <div className="space-y-2">
                  {['women', 'men', 'unisex'].map((gender) => (
                    <div key={gender} className="flex items-center space-x-2">
                      <Checkbox
                        id={gender}
                        checked={searchParams.get('gender') === gender}
                        onCheckedChange={(checked) => updateFilter('gender', checked ? gender : null)}
                        data-testid={`filter-gender-${gender}`}
                      />
                      <label htmlFor={gender} className="text-sm capitalize cursor-pointer">
                        {gender}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Price Range</Label>
                <div className="pt-4">
                  <Slider
                    min={0}
                    max={10000} // Increased max to match realistic prices
                    step={10}
                    value={priceRange}
                    onValueChange={setPriceRange}
                    data-testid="price-range-slider"
                  />
                  <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                    <span>₹{priceRange[0]}</span>
                    <span>₹{priceRange[1]}</span>
                  </div>
                </div>
              </div>

              {/* Quick Filters */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Quick Filters</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="trending"
                      checked={searchParams.get('trending') === 'true'}
                      onCheckedChange={(checked) => updateFilter('trending', checked ? 'true' : null)}
                      data-testid="filter-trending"
                    />
                    <label htmlFor="trending" className="text-sm cursor-pointer">
                      Trending
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="new"
                      checked={searchParams.get('is_new') === 'true'}
                      onCheckedChange={(checked) => updateFilter('is_new', checked ? 'true' : null)}
                      data-testid="filter-new"
                    />
                    <label htmlFor="new" className="text-sm cursor-pointer">
                      New Arrivals
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">Loading products...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <p className="text-muted-foreground">
                  {searchParams.get('search')
                    ? `No products found for "${searchParams.get('search')}"`
                    : "No products found"}
                </p>
                <Button onClick={clearFilters}>Clear Filters</Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-6">
                  {filteredProducts.length} {filteredProducts.length === 1 ? 'Product' : 'Products'} found
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-x-6 gap-y-10" data-testid="products-grid">
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};